import { BALANCE } from '../config/balance';
import type { Action } from '../config/controls';
import type { Input } from '../core/Input';
import { el } from './dom';

/** How far the thumb must travel before the stick reports movement. */
export const TOUCH_STICK_DEAD_ZONE = 0.12;

/**
 * The joystick's output for a thumb offset.
 *
 * The vector is scaled from the dead zone up to the rim so the stick stays
 * usable at small deflections; a full deflection is exactly one unit, the same
 * range the gamepad stick produces.
 */
export function stickVector(
  dx: number,
  dy: number,
  radius: number,
  deadZone = TOUCH_STICK_DEAD_ZONE,
): { x: number; y: number } {
  if (!(radius > 0)) return { x: 0, y: 0 };
  const distance = Math.hypot(dx, dy);
  if (distance < 1e-6) return { x: 0, y: 0 };
  const magnitude = Math.min(1, distance / radius);
  if (magnitude < deadZone) return { x: 0, y: 0 };
  const scaled = (magnitude - deadZone) / (1 - deadZone);
  // Screen y grows downward; the game's forward axis grows upward.
  return { x: (dx / distance) * scaled, y: (-dy / distance) * scaled };
}

interface TouchButtonSpec {
  action: Action;
  label: string;
  className: string;
  /** Hold repeats the action at the weapon cadence instead of firing once. */
  repeat?: boolean;
}

/**
 * The action cluster. The layout is a twin-thumb one: the stick under the left
 * thumb and every action under the right, with the weapon and jump on the
 * bottom row where they are easiest to reach. CSS reflows the cluster in
 * portrait, where the screen is narrow and the thumbs sit higher.
 */
const ACTION_BUTTONS: readonly TouchButtonSpec[] = [
  { action: 'fire', label: 'FIRE', className: 'touch-fire', repeat: true },
  { action: 'jump', label: 'JUMP', className: 'touch-jump' },
  { action: 'melee', label: 'CLAW', className: 'touch-melee' },
  { action: 'grapple', label: 'HOOK', className: 'touch-grapple' },
  { action: 'switchWeapon', label: 'NET', className: 'touch-net' },
  { action: 'sneak', label: 'SNEAK', className: 'touch-sneak' },
  { action: 'crouch', label: 'CROUCH', className: 'touch-crouch' },
  { action: 'grab', label: 'GRAB', className: 'touch-grab' },
];

/**
 * The room builder's palette, which replaces the action cluster while building.
 *
 * Placing is the grapple action: in a build session the right mouse button and
 * the PLACE button are the same request, so the pad needs no special case for
 * it. The rest are the builder's own keys.
 */
const BUILD_BUTTONS: readonly TouchButtonSpec[] = [
  { action: 'grapple', label: 'PLACE', className: 'touch-build-place' },
  { action: 'buildNext', label: 'PIECE', className: 'touch-build-piece' },
  { action: 'buildVariant', label: 'SIZE', className: 'touch-build-size' },
  { action: 'buildRotate', label: 'ROTATE', className: 'touch-build-rotate' },
  { action: 'buildMagnetic', label: 'MAGNET', className: 'touch-build-magnet' },
  { action: 'buildDelete', label: 'DELETE', className: 'touch-build-delete' },
  { action: 'buildDrone', label: 'DRONE', className: 'touch-build-drone' },
  { action: 'buildWaypoint', label: 'WAYPOINT', className: 'touch-build-waypoint' },
];

export interface TouchControlsCallbacks {
  /** Fired on the first touch, so the game can unlock audio. */
  onInteract?: () => void;
  /** Fired whenever a touch is seen, so a touch laptop can promote the UI. */
  onTouchSeen?: () => void;
}

/**
 * The on-screen twin-thumb interface for phones and tablets.
 *
 * It owns no gameplay state: the stick feeds the analog move axis and the drag
 * feeds the look accumulator on `Input`, while every button is an ordinary
 * injected action. That keeps the keyboard, the pad and the touchscreen on one
 * code path, and keeps this class testable in isolation.
 */
export class TouchControls {
  readonly root = el('div', 'touch-controls hidden');

  private readonly stick = el('div', 'touch-stick');
  private readonly knob = el('div', 'touch-stick-knob');
  /** Every button bound to an action: the same action can appear twice, as
   *  PLACE and HOOK both do while building. */
  private readonly buttons = new Map<Action, HTMLButtonElement[]>();
  private readonly held = new Set<Action>();
  private repeatTimer: number | null = null;
  private repeatAction: Action | null = null;
  private stickPointer: number | null = null;
  private lookPointer: number | null = null;
  private lookLastX = 0;
  private lookLastY = 0;
  /** Player-tuned multiplier on the drag rate; see `setLookSensitivity`. */
  private lookSensitivity: number = BALANCE.touch.lookScale;
  private readonly disposers: Array<() => void> = [];

  constructor(
    parent: HTMLElement,
    private readonly input: Input,
    private readonly canvas: HTMLCanvasElement,
    private readonly callbacks: TouchControlsCallbacks = {},
  ) {
    this.stick.append(el('div', 'touch-stick-ring'), this.knob);
    this.root.append(this.stick);

    const actions = el('div', 'touch-actions');
    for (const spec of ACTION_BUTTONS) actions.append(this.makeButton(spec));
    this.root.append(actions);

    const build = el('div', 'touch-build');
    for (const spec of BUILD_BUTTONS) build.append(this.makeButton(spec));
    this.root.append(build);

    this.root.append(
      this.makeButton({ action: 'toggleCamera', label: 'VIEW', className: 'touch-view' }),
      this.makeButton({ action: 'pause', label: 'PAUSE', className: 'touch-pause' }),
      this.makeButton({ action: 'swivelView', label: 'SETTLE', className: 'touch-settle' }),
    );

    parent.append(this.root);
    this.bindStick();
    this.bindLook();
    this.bindPromotion();
  }

  /** Show or hide the interface. Hiding it releases every held control. */
  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
    if (!visible) this.releaseAll();
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden');
  }

  /**
   * Set the player's touch look multiplier.
   *
   * It scales the base `BALANCE.touch.lookScale`, so a phone gets a much
   * longer turn per thumb drag than the fixed value used to allow.
   */
  setLookSensitivity(multiplier: number): void {
    if (Number.isFinite(multiplier)) {
      this.lookSensitivity = BALANCE.touch.lookScale * Math.max(0, multiplier);
    }
  }

  /** The buttons currently held, for tests and tooling. */
  get heldActions(): Action[] {
    return [...this.held];
  }

  dispose(): void {
    this.releaseAll();
    for (const dispose of this.disposers) dispose();
    this.disposers.length = 0;
    this.root.remove();
  }

  // ------------------------------------------------------------------ buttons

  private makeButton(spec: TouchButtonSpec): HTMLButtonElement {
    const button = el('button', `touch-button ${spec.className}`, spec.label);
    button.type = 'button';
    button.dataset.action = spec.action;

    const press = (event: PointerEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      capturePointer(button, event.pointerId);
      this.press(spec);
    };
    const release = (): void => this.release(spec.action);

    button.addEventListener('pointerdown', press);
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);
    button.addEventListener('contextmenu', (event) => event.preventDefault());

    const existing = this.buttons.get(spec.action) ?? [];
    existing.push(button);
    this.buttons.set(spec.action, existing);
    return button;
  }

  private press(spec: TouchButtonSpec): void {
    this.held.add(spec.action);
    for (const button of this.buttons.get(spec.action) ?? []) button.classList.add('pressed');
    this.input.inject(spec.action, true);
    // Touch-only: a held fire button keeps firing at the rifle's own cadence,
    // which is what a thumb expects. The desktop stays one shot per click.
    if (spec.repeat) this.startRepeat(spec.action);
    this.callbacks.onInteract?.();
  }

  private release(action: Action): void {
    if (!this.held.has(action)) return;
    this.held.delete(action);
    for (const button of this.buttons.get(action) ?? []) button.classList.remove('pressed');
    this.input.inject(action, false);
    if (this.repeatAction === action) this.stopRepeat();
  }

  /**
   * Swap the action cluster for the room builder's palette.
   *
   * The stick and the system buttons stay where they are: walking around the
   * deck being built is the point, and the gameplay cluster has nothing to do
   * while nothing can be shot.
   */
  setBuildModel(active: boolean): void {
    this.root.classList.toggle('build-active', active);
    if (!active) this.releaseAll();
  }

  private startRepeat(action: Action): void {
    this.stopRepeat();
    this.repeatAction = action;
    const interval = Math.max(16, BALANCE.combat.fireInterval * 1000);
    this.repeatTimer = window.setInterval(() => {
      if (this.root.classList.contains('hidden')) return;
      this.input.inject(action, true);
    }, interval);
  }

  private stopRepeat(): void {
    if (this.repeatTimer !== null) window.clearInterval(this.repeatTimer);
    this.repeatTimer = null;
    this.repeatAction = null;
  }

  private releaseAll(): void {
    this.stopRepeat();
    for (const action of [...this.held]) this.release(action);
    this.held.clear();
    this.resetStick();
    this.lookPointer = null;
  }

  // -------------------------------------------------------------------- stick

  private bindStick(): void {
    const down = (event: PointerEvent): void => {
      if (this.stickPointer !== null) return;
      event.preventDefault();
      event.stopPropagation();
      this.stickPointer = event.pointerId;
      capturePointer(this.stick, event.pointerId);
      this.moveStick(event);
      this.callbacks.onInteract?.();
    };
    const move = (event: PointerEvent): void => {
      if (event.pointerId !== this.stickPointer) return;
      event.preventDefault();
      this.moveStick(event);
    };
    const up = (event: PointerEvent): void => {
      if (event.pointerId !== this.stickPointer) return;
      this.resetStick();
      releasePointer(this.stick, event.pointerId);
    };

    this.stick.addEventListener('pointerdown', down);
    this.stick.addEventListener('pointermove', move);
    this.stick.addEventListener('pointerup', up);
    this.stick.addEventListener('pointercancel', up);
    this.stick.addEventListener('lostpointercapture', up);
  }

  private moveStick(event: PointerEvent): void {
    const rect = this.stick.getBoundingClientRect();
    const radius = Math.max(1, Math.min(rect.width, rect.height) * 0.5);
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    const vector = stickVector(dx, dy, radius);
    this.input.setTouchMove(vector.x, vector.y);

    // The knob follows the thumb, but never leaves the ring.
    const limit = radius * 0.7;
    const distance = Math.hypot(dx, dy);
    const scale = distance > limit ? limit / distance : 1;
    this.knob.style.transform = `translate(${dx * scale}px, ${dy * scale}px)`;
  }

  private resetStick(): void {
    this.stickPointer = null;
    this.input.setTouchMove(0, 0);
    this.knob.style.transform = '';
  }

  // --------------------------------------------------------------------- look

  private bindLook(): void {
    const down = (event: PointerEvent): void => {
      // The mouse path is owned by `Input`; only real touches drag the view.
      if (event.pointerType === 'mouse') return;
      if (this.lookPointer !== null) return;
      this.lookPointer = event.pointerId;
      this.lookLastX = event.clientX;
      this.lookLastY = event.clientY;
      capturePointer(this.canvas, event.pointerId);
      this.callbacks.onInteract?.();
    };
    const move = (event: PointerEvent): void => {
      if (event.pointerId !== this.lookPointer) return;
      const dx = event.clientX - this.lookLastX;
      const dy = event.clientY - this.lookLastY;
      this.lookLastX = event.clientX;
      this.lookLastY = event.clientY;
      if (dx === 0 && dy === 0) return;
      this.input.addTouchLook(dx * this.lookSensitivity, dy * this.lookSensitivity);
    };
    const up = (event: PointerEvent): void => {
      if (event.pointerId !== this.lookPointer) return;
      this.lookPointer = null;
      releasePointer(this.canvas, event.pointerId);
    };

    this.canvas.addEventListener('pointerdown', down);
    this.canvas.addEventListener('pointermove', move);
    this.canvas.addEventListener('pointerup', up);
    this.canvas.addEventListener('pointercancel', up);
  }

  // ---------------------------------------------------------------- promotion

  private bindPromotion(): void {
    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType === 'touch') this.callbacks.onTouchSeen?.();
    };
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    this.disposers.push(() => window.removeEventListener('pointerdown', onPointerDown));
  }
}

/**
 * Pointer capture keeps a drag attached to the control it started on, but some
 * browsers throw for an already-released pointer, and a capture is not worth
 * a broken input frame.
 */
function capturePointer(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture?.(pointerId);
  } catch {
    // The pointer is already gone; the release path handles it.
  }
}

function releasePointer(element: HTMLElement, pointerId: number): void {
  try {
    element.releasePointerCapture?.(pointerId);
  } catch {
    // Nothing was captured.
  }
}
